import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Banknote, Scale, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatUsd } from '@/lib/money'
import type { Product, WeighQuote, WeightUnit } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal, Tabs } from '@/components/ui/primitives'
import { t } from '@/i18n'

/** Largest first, the way a shop thinks about them. */
const UNITS: { value: WeightUnit; label: string; short: string }[] = [
  { value: 'Tonne', label: t('Tonnes'), short: 't' },
  { value: 'Kilogram', label: t('Kilograms'), short: 'kg' },
  { value: 'Gram', label: t('Grams'), short: 'g' },
]

type Mode = 'weight' | 'amount'

/**
 * Asks how much of a scale item is being sold, from either end.
 *
 * A cashier either weighs the goods and wants the price, or is handed a note and wants to know
 * what weight it buys. Both are the same question and the server answers both, so the two can
 * never disagree about what a kilo costs.
 */
export function WeighDialog({
  product,
  onClose,
  onConfirm,
}: {
  product: Product | null
  onClose: () => void
  /**
   * The weight as entered plus the same weight in the product's stock unit. The first is what
   * gets sent to checkout; the second only keeps the running total on screen honest.
   */
  onConfirm: (quantity: number, unit: WeightUnit, stockQuantity: number) => void
}) {
  const [mode, setMode] = useState<Mode>('weight')
  const [unit, setUnit] = useState<WeightUnit>('Kilogram')
  const [value, setValue] = useState('')

  useEffect(() => {
    if (product) {
      setMode('weight')
      // Starts in the unit the item is actually held in, which is the one that always works —
      // any other may be too fine for the item to record.
      setUnit(isWeightUnit(product.unit) ? product.unit : 'Kilogram')
      setValue('')
    }
  }, [product])

  const typed = Number(value || 0)

  // Debounced against the server, which owns the arithmetic in both directions. The till never
  // multiplies a price by a weight itself.
  const [debounced, setDebounced] = useState(0)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(typed), 250)
    return () => clearTimeout(timer)
  }, [typed])

  const quote = useQuery({
    queryKey: ['weigh', product?.id, mode, unit, debounced],
    queryFn: () => {
      const key = mode === 'weight' ? 'quantity' : 'amountUsd'
      return apiClient.get<WeighQuote>(
        `/api/products/${product!.id}/weigh?${key}=${debounced}&unit=${unit}`,
      )
    },
    enabled: product !== null && debounced > 0,
    retry: false,
  })

  const short = UNITS.find((option) => option.value === unit)?.short ?? ''
  const problem =
    quote.error instanceof ApiError ? (quote.error.detail ?? quote.error.message) : null

  return (
    <Modal
      open={product !== null}
      onClose={onClose}
      title={product?.name ?? ''}
      description={
        product
          ? `${formatUsd(product.priceUsd)} per ${abbreviate(product.unit)} · ${product.quantityOnHand} ${abbreviate(product.unit)} on hand`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() =>
              onConfirm(quote.data!.quantity, quote.data!.unit, quote.data!.stockQuantity)
            }
            disabled={!quote.data || quote.isFetching}
          >
            {t('Add to cart')}
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <Tabs
          tabs={[
            { value: 'weight' as const, label: t('By weight') },
            { value: 'amount' as const, label: t('By amount') },
          ]}
          value={mode}
          onChange={(next) => {
            setMode(next)
            setValue('')
          }}
        />
      </div>

      {mode === 'weight' ? (
        <>
          {/* The unit is chosen before the number, because it changes what the number means. */}
          <div className="mb-3 flex flex-wrap gap-2">
            {UNITS.map((option) => (
              <Button
                key={option.value}
                variant={unit === option.value ? 'primary' : 'secondary'}
                size="sm"
                // Full touch height: choosing grams instead of kilograms is a thousandfold
                // difference, and it is chosen on a phone, one-handed, with a queue waiting.
                className="touch-target flex-1"
                onClick={() => setUnit(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>

          <Input
            label={`Weight in ${short}`}
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            leading={<Scale className="h-4 w-4" />}
            className="tabular text-lg"
            placeholder="0"
            autoFocus
          />
        </>
      ) : (
        <>
          <Input
            label={t('Amount in US dollars')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            leading={<Banknote className="h-4 w-4" />}
            className="tabular text-lg"
            placeholder="0.00"
            autoFocus
          />

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="self-center text-sm text-[var(--text-secondary)]">{t('Weigh out in')}</span>
            {UNITS.map((option) => (
              <Button
                key={option.value}
                variant={unit === option.value ? 'primary' : 'secondary'}
                size="sm"
                // Full touch height: choosing grams instead of kilograms is a thousandfold
                // difference, and it is chosen on a phone, one-handed, with a queue waiting.
                className="touch-target flex-1"
                onClick={() => setUnit(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </>
      )}

      <div className="surface-sunken mt-5 rounded-xl p-4">
        {problem ? (
          // The server's own words: it knows why a weight will not go, and what to do instead.
          <p role="alert" className="text-sm text-danger-500">
            <TriangleAlert className="mr-1 inline h-4 w-4" />
            {problem}
          </p>
        ) : quote.data ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="tabular text-lg font-medium">
                {trim(quote.data.quantity)} {short}
              </span>
              <span className="tabular text-2xl font-semibold">
                {formatUsd(quote.data.priceUsd)}
              </span>
            </div>

            {/* Said out loud rather than hidden: a weight cannot always land on a round amount. */}
            {quote.data.isApproximate && (
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                Nearest weight to {formatUsd(typed)} — a scale cannot divide any finer.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-[var(--text-secondary)]">
            {mode === 'weight' ? 'Enter a weight to price it.' : 'Enter an amount to weigh it out.'}
          </p>
        )}
      </div>
    </Modal>
  )
}

function isWeightUnit(unit: string): unit is WeightUnit {
  return unit === 'Tonne' || unit === 'Kilogram' || unit === 'Gram'
}

export function abbreviate(unit: string): string {
  switch (unit) {
    case 'Tonne':
      return 't'
    case 'Kilogram':
      return 'kg'
    case 'Gram':
      return 'g'
    case 'Litre':
      return 'L'
    case 'Millilitre':
      return 'ml'
    case 'Metre':
      return 'm'
    default:
      return ''
  }
}

/** Drops trailing zeros so 0.300 reads as 0.3. */
export function trim(value: number): string {
  return String(Math.round(value * 1000) / 1000)
}
